import { DOCUMENT, Injectable, OnDestroy, Renderer2, computed, inject, signal } from '@angular/core';
import { DeviceDetectionService } from 'src/app/core/services/device-detection/device-detection.service';

interface BreadCrumbInfo {
  id: string;
  includeBreadcrumb: boolean;
}

interface AlphabetFilterInfo {
  id: string;
  includeAlphabetFilter: boolean;
}

interface PageSectionStyle {
  [key: string]: any;
}

@Injectable({
  providedIn: 'root'
})
export class ListingPageService implements OnDestroy {
  private readonly deviceDetectionService = inject(DeviceDetectionService);
  private document = inject<Document>(DOCUMENT);

  private _breadCrumbInfo = signal<BreadCrumbInfo>({ id: "", includeBreadcrumb: false });
  readonly breadCrumbInfo = this._breadCrumbInfo.asReadonly();

  private _alphabetFilterInfo = signal<AlphabetFilterInfo>({ id: "", includeAlphabetFilter: false });
  readonly alphabetFilterInfo = this._alphabetFilterInfo.asReadonly();

  private _previewTemplate = signal<Array<any>>([]);
  readonly previewTemplate = this._previewTemplate.asReadonly();

  private _emptyPreviewTemplate = signal<boolean>(false);
  readonly emptyPreviewTemplate = this._emptyPreviewTemplate.asReadonly();

  // Computed signals for derived state
  readonly hasPreviewTemplate = computed(() => this._previewTemplate().length > 0);
  readonly previewTemplateCount = computed(() => this._previewTemplate().length);
  readonly hasBreadcrumb = computed(() => this._breadCrumbInfo().includeBreadcrumb);
  readonly hasAlphabetFilter = computed(() => this._alphabetFilterInfo().includeAlphabetFilter);

  private renderer!: Renderer2;
  private timeoutId: ReturnType<typeof setTimeout> | null = null;

  /**
   * Reset the preview template to empty array
   */
  resetPreviewTemplate(): void {
    this._previewTemplate.set([]);
  }

  /**
   * Render the page with template data and apply theme configuration
   */
  renderPage(renderer: Renderer2, templateData: any[]): void {
    try {
      this.renderer = renderer;
      templateData = this.removeSectionBasedOnDevice(templateData);


      const templateWithoutHeaderData = templateData[0];

      // Process breadcrumb and alphabet filter
      const processedTemplate = this.checkForBreadCrumbAndAlphabetFilter(templateWithoutHeaderData);
      this._previewTemplate.set(processedTemplate);

      // Update styles
      this.updateHomePageStyle(templateData[1]);

    } catch (error) {
      this._previewTemplate.set([]);
    }
  }

  /**
   * Process template data to extract breadcrumb and alphabet filter information
   */
  private checkForBreadCrumbAndAlphabetFilter(templateData: any[]): any[] {
    try {
      const filteredTemplate = templateData.reduce((accumulator: any[], section: any) => {
        // Skip null or undefined sections
        if (section === null || section === undefined) {
          return accumulator;
        }

        // If section is a string (HTML content)
        if (typeof section === 'string') {
          const idMatch = section.match(/<section\s+id=["']([^"']+)["']/);
          const sectionId = idMatch?.[1] ?? '';

          if (section.includes('breadcrumb-navigation')) {
            const breadCrumbInfo = { id: sectionId, includeBreadcrumb: true };
            this._breadCrumbInfo.set(breadCrumbInfo);
            return accumulator; // Skip this section
          }

          if (section.includes('alphabet-selector')) {
            const alphabetFilterInfo = { id: sectionId, includeAlphabetFilter: true };
            this._alphabetFilterInfo.set(alphabetFilterInfo);
            return accumulator; // Skip this section
          }

          // Keep the original HTML string for other sections
          accumulator.push(section);
        } else if (typeof section === 'object' && section !== null) {
          // For object sections, ensure they have required properties before including
          if (section.categoryLayoutConfig || section.productCardData || section.theme) {
            accumulator.push(section);
          }
        }

        return accumulator;
      }, []);

      return filteredTemplate;
    } catch (error) {
      return templateData;
    }
  }

  /**
   * Remove sections based on device type
   */
  private removeSectionBasedOnDevice(templateData: any[]): any[] {
    try {
      // Use DeviceDetectionService signals instead of BreakpointObserver
      const deviceType = this.deviceDetectionService.deviceType();

      if (deviceType === 'mobile' || deviceType === 'tablet') {
        const wordToFind = 'hide-on-mobile';
        const filteredData = templateData?.[0]?.filter((templateDatum: any) => {
          if (typeof templateDatum === 'string') {
            return !templateDatum.includes(wordToFind);
          } else {
            return templateDatum;
          }
        });
        // Replace webStoreTemplate[0] with filtered data
        templateData[0] = [...filteredData];
      } else if (deviceType === 'desktop') {
        const wordToFind = 'hide-on-desktop';
        const filteredData = templateData?.[0]?.filter((templateDatum: any) => {
          if (typeof templateDatum === 'string') {
            return !templateDatum.includes(wordToFind);
          } else {
            return templateDatum;
          }
        });
        // Replace webStoreTemplate[0] with filtered data
        templateData[0] = [...filteredData];
      }

      return templateData;
    } catch (error) {
      return templateData;
    }
  }

  /**
   * Apply theme styles to the page
   * COMMENTED OUT: Theme should be handled by individual page components, not by listing service
   */




  /**
   * Update home page styles
   */
  private updateHomePageStyle(pageSectionStyle: PageSectionStyle): void {
    try {
      Object.entries(pageSectionStyle).forEach(([keyframes, value]) => {
        if (Array.isArray(value)) {
          this.updateStyle(value);
        } else {
          if (keyframes === 'footerStyle' && typeof value === 'string') {
            const selectorRegex = /\.([^\s,{]+)/g; // Match class names before space, { or ,

            // Extract all class selectors from the CSS string
            const matches = value.match(selectorRegex);

            if (matches && matches.length > 0) {
              const firstClass = matches[0].replace('.', ''); // Remove dot from class name
              this.updateStyle([{ [firstClass]: value }]);
            }
          }
        }
      });
    } catch (error) {
      // Failed to update home page style
    }
  }

  /**
   * Append style to document head
   */
  private appendStyle(sectionId: string, styles: string): void {
    try {
      const style = this.renderer.createElement('style');
      style.id = sectionId;
      const text = this.renderer.createText(styles || "");
      this.renderer.appendChild(style, text);
      const head = this.document.getElementsByTagName('head')[0];
      this.renderer.appendChild(head, style);
    } catch (error) {
      // Failed to append style
    }
  }

  /**
   * Clear timeout and reset
   */
  clearTimeout(): void {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = setTimeout(() => { }, 0);
    }
  }

  /**
   * Update styles for various components
   */
  private updateStyle(styles: Record<string, any>[]): void {
    try {
      // Ensure carouselListStyles is an array and has elements
      if (Array.isArray(styles) && styles.length > 0) {
        styles.forEach((styleVal: Record<string, any>) => {
          // Ensure styleVal is an object
          if (typeof styleVal === 'object' && styleVal !== null) {
            const entries = Object.entries(styleVal);
            // Ensure styleVal has at least one entry
            if (entries.length > 0) {
              const firstEntry = entries[0]!;
              const [key, value] = firstEntry; // Extracting the key-value pair

              const originalKey = key;
              const modifiedKey = originalKey.replace("style-", "");

              setTimeout(() => {
                const sectionToAddClass = this.document.getElementById(modifiedKey);
                if (sectionToAddClass) {
                  sectionToAddClass.classList.add(key);
                }
              }, 500);

              // Ensure key is a string and value is defined
              if (typeof key === 'string' && typeof value !== 'undefined') {
                this.appendStyle(key, value as string);
              }
            }
          }
        });
      }
    } catch (error) {
      // Failed to update style
    }
  }

  ngOnDestroy(): void {
    this.clearTimeout();
  }
}


